import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ensureCsrfCookie } from "@/lib/api-client";
import { queryClient } from "@/lib/query-provider";
import { useAuthStore } from "@/store/auth-store";
import type { Membership, User } from "@/store/auth-store";

interface Paginated<T> {
  items: T[];
  count: number;
  limit: number;
  offset: number;
}

export function useLogin() {
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      // Bootstrap CSRF before the first unsafe request — the cookie has
      // to exist client-side so ``ky`` can echo it as ``X-CSRFToken``.
      await ensureCsrfCookie();
      const user = await api.post("auth/login", { json: credentials }).json<User>();

      const { setAuthenticated, setOrganization, setLocation } = useAuthStore.getState();
      setAuthenticated(true);
      // Drop any stale locationId hydrated from sessionStorage so the
      // auto-select / picker flow runs from a clean slate on every login.
      setLocation(null);

      const membershipsPage = await api.get("memberships/").json<Paginated<Membership>>();
      const memberships = membershipsPage.items;

      // Seed both auth/me (useSession) and user/me (useCurrentUser) caches —
      // the login response is the source of truth for both. Without
      // ["auth","me"], AuthenticatedShell would mount with a stale error
      // cache from /login and bounce back to /login for one frame.
      queryClient.setQueryData(["auth", "me"], user);
      queryClient.setQueryData(["user", "me"], user);
      queryClient.setQueryData(["memberships"], memberships);

      const defaultOrg = memberships.find((m) => m.is_default) ?? memberships[0];
      if (defaultOrg) {
        setOrganization(defaultOrg.organization_id);
      }

      return { user, memberships };
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      // Best-effort: even if the server rejects the call the local state
      // gets cleared so the user lands on /login.
      try {
        await api.post("auth/logout");
      } catch {
        // ignore — local cleanup runs regardless
      }
      useAuthStore.getState().logout();
      queryClient.clear();
    },
  });
}

/**
 * Verifies the session is still alive on the server and restores
 * ``isAuthenticated`` + ``organizationId`` in the store from the cookie.
 *
 * Required for any tab that didn't go through ``useLogin``: a fresh tab
 * (Cmd+T) has empty ``sessionStorage`` but inherits the session cookie,
 * so without this rehydration ``organizationId`` would stay ``null`` and
 * every query gated on it (locations, zones, …) would never fire.
 */
export function useSession() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const user = await api.get("auth/me").json<User>();

      const { setAuthenticated, setOrganization, organizationId } = useAuthStore.getState();
      setAuthenticated(true);

      if (!organizationId) {
        const membershipsPage = await api.get("memberships/").json<Paginated<Membership>>();
        const memberships = membershipsPage.items;
        queryClient.setQueryData(["memberships"], memberships);
        const defaultOrg = memberships.find((m) => m.is_default) ?? memberships[0];
        if (defaultOrg) setOrganization(defaultOrg.organization_id);
      }

      return user;
    },
    retry: false,
    staleTime: Infinity,
  });
}
